import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft } from "lucide-react";

// Modale "tour" leggero multi-step. Auto mostra alla prima visita (storageKey su localStorage).
// Riapribile passando forceShow=true (usando anche key per remount dal parent).
export default function IntroTour({ storageKey, steps, accent = "#7C3AED", ctaLabel, onCta, forceShow = false }) {
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);

  useEffect(() => {
    if (forceShow) { setOpen(true); setI(0); return; }
    try { if (!localStorage.getItem(storageKey)) setOpen(true); } catch { setOpen(true); }
  }, [storageKey, forceShow]);

  const finish = () => {
    try { localStorage.setItem(storageKey, "1"); } catch {}
    setOpen(false);
  };

  if (!open) return null;
  const step = steps[i];
  const Icon = step.icon;
  const last = i === steps.length - 1;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ background: "rgba(15,15,20,0.55)", backdropFilter: "blur(4px)" }}
        data-testid="intro-tour"
      >
        <motion.div
          initial={{ y: 40, scale: 0.96, opacity: 0 }} animate={{ y: 0, scale: 1, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 28 }}
          className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden relative"
        >
          <button onClick={finish} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center text-zinc-500 z-10" data-testid="intro-tour-close">
            <X className="w-4 h-4" />
          </button>

          <div className="px-6 pt-8 pb-6 text-center">
            <motion.div key={i} initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center text-white shadow-lg" style={{ background: accent }}>
              <Icon className="w-8 h-8" />
            </motion.div>
            <motion.h3 key={`t${i}`} initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-xl font-black text-zinc-900 mb-2 leading-tight">{step.title}</motion.h3>
            <motion.p key={`d${i}`} initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.05 }} className="text-sm text-zinc-600 leading-relaxed">{step.text}</motion.p>
          </div>

          {/* dots */}
          {steps.length > 1 && (
            <div className="flex items-center justify-center gap-1.5 pb-4">
              {steps.map((_, idx) => (
                <span key={idx} className="h-1.5 rounded-full transition-all" style={{ width: idx === i ? 20 : 6, background: idx === i ? accent : "#e4e4e7" }} />
              ))}
            </div>
          )}

          <div className="px-6 pb-6 flex items-center gap-2">
            {i > 0 ? (
              <button onClick={() => setI(i - 1)} className="px-4 py-3 rounded-full bg-zinc-100 text-zinc-700 font-bold text-sm inline-flex items-center gap-1" data-testid="intro-tour-prev"><ArrowLeft className="w-4 h-4" /></button>
            ) : (
              <button onClick={finish} className="px-4 py-3 rounded-full text-zinc-400 font-bold text-sm" data-testid="intro-tour-skip">Salta</button>
            )}
            <div className="flex-1" />
            {!last ? (
              <button onClick={() => setI(i + 1)} className="px-6 py-3 rounded-full text-white font-black text-sm inline-flex items-center gap-1.5 shadow-lg" style={{ background: accent }} data-testid="intro-tour-next">
                Avanti <ArrowRight className="w-4 h-4" />
              </button>
            ) : ctaLabel ? (
              <button onClick={() => { finish(); onCta && onCta(); }} className="px-6 py-3 rounded-full text-white font-black text-sm inline-flex items-center gap-1.5 shadow-lg" style={{ background: accent }} data-testid="intro-tour-cta">
                {ctaLabel} <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={finish} className="px-6 py-3 rounded-full text-white font-black text-sm shadow-lg" style={{ background: accent }} data-testid="intro-tour-finish">Ho capito</button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
